"use client";

import { ScrollContainer } from "@/components/platform/scroll-container";
import EmptyState from "@/components/portfolio/empty-state";
import { ChevronDown } from "@/components/svg";
import { LiveIcon } from "@/components/ui/live-icon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SelectionIndicator } from "@/components/ui/selection-indicator";
import { CryptoEventData, CryptoGuess } from "@/event-core/types";
import { formatLocalDate } from "@/utils/formatLocalDate";
import { get } from "@/utils/utils";
import { useCountDown } from "ahooks";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePastThreeGuesses } from "./hooks/use-past-three-guesses";
import { useTimelinePopover } from "./hooks/use-timeline-popover";

/**
 * 格式化开始时间
 * @param scheduleStartTime - ISO 时间字符串
 * @returns 格式化后的文本（Today、Yesterday 或 MMM D）
 */
function formatScheduleStartTime( scheduleStartTime: number ): string {
    if ( !scheduleStartTime ) return "";

    const date = dayjs( formatLocalDate( scheduleStartTime, "YYYY-MM-DD" ) );
    const today = dayjs( formatLocalDate( Date.now(), "YYYY-MM-DD" ) );

    if ( date.isSame( today, "day" ) ) {
        return "Today";
    }
    return formatLocalDate( scheduleStartTime, "MMM D" );
}

interface TimelineItem {
    type: "up" | "down" | "time" | "active" | "more";
    // time display
    time?: string;
    selected?: boolean;
    guessNo?: string;
}

interface TimelinePopoverProps {
    popover: ReturnType<typeof useTimelinePopover>;
    align: "start" | "end";
    selectedGuessNo?: string;
    onGuessChange?: ( guess: CryptoGuess ) => void;
    onOpenChange?: ( open: boolean ) => void;
    emptyMessage: string;
}

function TimelinePopover(
    { popover, align, selectedGuessNo, onGuessChange, onOpenChange, emptyMessage }: TimelinePopoverProps,
) {
    return (
        <PopoverContent
            className="w-[220px] h-[200px] p-2 rounded-lg mt-1 bg-brand-button shadow-[0_4px_12px_0_rgba(0,0,0,0.25)] border-stroke overflow-y-auto"
            side="bottom"
            align={align}
            onScroll={popover.handleScroll}
        >
            {popover.loading && popover.guesses.length === 0
                ? <div className="p-4 text-center text-t3 text-sm">Loading...</div>
                : popover.options.length > 0
                ? (
                    <div className="flex flex-col gap-1">
                        {popover.options.map( ( option ) => (
                            <div
                                key={option.value}
                                className="flex items-center gap-2 p-2 rounded-lg hover:bg-surface-muted cursor-pointer"
                                onClick={() => {
                                    onGuessChange?.( option.guess );
                                    onOpenChange?.( false );
                                }}
                            >
                                <SelectionIndicator selected={selectedGuessNo === option.value} />
                                <div className="flex items-center gap-1">
                                    <span className="text-sm text-t1 font-semibold">{option.label}</span>
                                    <svg viewBox="0 0 3 3" fill="none" className="size-[3px]">
                                        <circle cx="1.5" cy="1.5" r="1.5" fill="#77808D" />
                                    </svg>
                                    <span className="text-sm text-t3 font-semibold">
                                        {formatScheduleStartTime( option.guess.scheduleStartTime || 0 )}
                                    </span>
                                </div>
                            </div>
                        ) )}
                        {popover.loading && <div className="p-2 text-center text-t3 text-xs">Loading more...</div>}
                    </div>
                )
                : <EmptyState text={emptyMessage} className="h-auto py-4" />}
        </PopoverContent>
    );
}

export function TimeTimeline(
    { guessList, eventData, onGuessChange, selectedGuess, onUpdateGuessList }: {
        guessList: CryptoGuess[];
        eventData: CryptoEventData;
        onGuessChange?: ( guess: CryptoGuess ) => void;
        selectedGuess?: CryptoGuess | null;
        onUpdateGuessList?: () => void;
    },
) {
    const [ isPastOpen, setIsPastOpen ] = useState( false );
    const [ selectedPastGuess, setSelectedPastGuess ] = useState<CryptoGuess | null>( null );
    // const [ isFutureOpen, setIsFutureOpen ] = useState( false );

    const eventNo = get( eventData, "eventNo", "" );
    const expo = get( eventData, "expo", 0 );

    const currentSelectedNo = get( selectedGuess, "guessNo", "" );

    const firstId = guessList.length > 0 ? Number( get( guessList[0], "id", 0 ) ) : 0;
    const lastId = guessList.length > 0 ? Number( get( guessList[guessList.length - 1], "id", 0 ) ) : 0;

    const past = useTimelinePopover( eventNo, true, firstId );
    const future = useTimelinePopover( eventNo, false, lastId );

    const mainGuess = useMemo( () => {
        return guessList.find( ( guess: CryptoGuess ) => get( guess, "mainFlag", 0 ) );
    }, [ guessList ] );

    const pastStatuses = usePastThreeGuesses( eventNo, mainGuess, expo );
    const scrollContainerRef = useRef<HTMLDivElement>( null );

    useEffect( () => {
        if ( currentSelectedNo && scrollContainerRef.current ) {
            const container = scrollContainerRef.current;
            const selectedElement = container.querySelector( `[data-guess-no="${currentSelectedNo}"]` ) as HTMLElement;

            if ( selectedElement ) {
                const targetScrollLeft = selectedElement.offsetLeft
                    - container.clientWidth / 2
                    + selectedElement.clientWidth / 2;

                container.scrollTo( { left: targetScrollLeft, behavior: "smooth" } );
            }
        }
    }, [ currentSelectedNo ] );

    const [ countdown ] = useCountDown( {
        targetDate: mainGuess?.scheduleEndTime,
        onEnd: () => {
            if ( onUpdateGuessList ) {
                setTimeout( () => {
                    onUpdateGuessList();
                }, 2000 );
            }
        },
    } );

    const timelineData = useMemo<TimelineItem[]>( () => {
        const baseData = guessList.map( ( guess: CryptoGuess, index: number ) => {
            const scheduleStartTime = get( guess, "scheduleStartTime", "" );
            const time = formatLocalDate( scheduleStartTime, "h:mmA" );
            const guessNo = get( guess, "guessNo", "" );

            const mainFlag = get( guess, "mainFlag", 0 );
            const status = get( guess, "status", 0 );

            // 如果 live 标正好在列表的最后，需要额外判断状态是否不为 1，如果状态不为 1 说明它不是正在进行的，这情况不能展示 live 标
            const isLive = mainFlag && ( index !== guessList.length - 1 || status === 1 );

            return {
                type: isLive ? "active" : "time",
                time,
                guessNo,
                selected: guessNo === currentSelectedNo,
            } as TimelineItem;
        } );

        // 检查当前选中的 ID 是否在主列表中
        const isInList = guessList.some( ( g: CryptoGuess ) => get( g, "guessNo", "" ) === currentSelectedNo );

        // UI 补偿：如果不在列表中，且存在选中的竞猜对象
        if ( !isInList && currentSelectedNo ) {
            // 优先使用外部传入的 selectedGuess，其次是本地弹窗选中的项
            const compensationGuess = ( selectedGuess && get( selectedGuess, "guessNo", "" ) === currentSelectedNo )
                ? selectedGuess
                : ( selectedPastGuess && get( selectedPastGuess, "guessNo", "" ) === currentSelectedNo )
                ? selectedPastGuess
                : null;

            if ( compensationGuess ) {
                const scheduleStartTime = get( compensationGuess, "scheduleStartTime", "" );
                const time = formatLocalDate( scheduleStartTime, "h:mmA" );
                const guessNo = get( compensationGuess, "guessNo", "" );
                const pastItem: TimelineItem = { type: "time", time, guessNo, selected: true };
                return [ pastItem, ...baseData ];
            }
        }

        return baseData;
    }, [ guessList, currentSelectedNo, selectedGuess, selectedPastGuess ] );

    return (
        <div className="flex w-full items-center my-3">
            {/* Left Fixed Section */}
            <div className="flex gap-1 bg-brand-button h-8 rounded-full items-center">
                <Popover open={isPastOpen} onOpenChange={setIsPastOpen}>
                    <PopoverTrigger asChild>
                        <div
                            className="flex flex-shrink-0 items-center gap-1 cursor-pointer pl-3 relative"
                            onClick={past.handleLoad}
                        >
                            <span className="text-xs font-medium text-t1">Past</span>
                            <ChevronDown className="h-4 w-4 text-t3" />
                        </div>
                    </PopoverTrigger>
                    <TimelinePopover
                        popover={past}
                        align="start"
                        selectedGuessNo={currentSelectedNo}
                        onGuessChange={( guess ) => {
                            setSelectedPastGuess( guess );
                            onGuessChange?.( guess );
                        }}
                        onOpenChange={setIsPastOpen}
                        emptyMessage="No past data available"
                    />
                </Popover>
                <div className="h-5 w-[1px] flex-shrink-0 bg-stroke-subtle" />
                <div className="flex items-center gap-1 pr-3">
                    {pastStatuses.length > 0
                        ? ( pastStatuses.map( ( status, index ) => (
                            <TimelineNode key={index} item={{ type: status.type }} />
                        ) ) )
                        : (
                            <>
                                <TimelineNode item={{ type: "down" }} />
                                <TimelineNode item={{ type: "up" }} />
                                <TimelineNode item={{ type: "up" }} />
                            </>
                        )}
                </div>
            </div>
            <div className="flex-1 min-w-0 ">
                <ScrollContainer ref={scrollContainerRef} className="flex items-center gap-2  pl-2 pr-2 ">
                    {timelineData.map( ( item, index ) => {
                        const guess = guessList.find( ( g: CryptoGuess ) => get( g, "guessNo", "" ) === item.guessNo )
                            || ( get( selectedGuess, "guessNo", "" ) === item.guessNo ? selectedGuess : undefined )
                            || ( selectedPastGuess && get( selectedPastGuess, "guessNo", "" ) === item.guessNo
                                ? selectedPastGuess
                                : undefined );
                        return (
                            <TimelineNode
                                key={index}
                                item={item}
                                onGuessChange={guess
                                    ? () => onGuessChange?.( guess )
                                    : undefined}
                                guessNo={item.guessNo}
                            />
                        );
                    } )}
                    {
                        /* <Popover open={isFutureOpen} onOpenChange={setIsFutureOpen}>
                        <PopoverTrigger asChild>
                            <div onClick={future.handleLoad}>
                                <TimelineNode
                                    item={{
                                        type: "more",
                                    }}
                                />
                            </div>
                        </PopoverTrigger>
                        <TimelinePopover
                            popover={future}
                            align="end"
                            selectedGuessNo={currentSelectedNo}
                            onGuessChange={onGuessChange}
                            onOpenChange={setIsFutureOpen}
                            emptyMessage="No future data available"
                        />
                    </Popover> */
                    }
                </ScrollContainer>
            </div>
        </div>
    );
}

function TimelineNode(
    { item, onGuessChange, guessNo }: { item: TimelineItem; onGuessChange?: () => void; guessNo?: string },
) {
    if ( item.type === "up" ) {
        return (
            <div className="flex size-5 flex-shrink-0 items-center justify-center rounded-full bg-action-accent">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path
                        d="M4.75133 2.82404C5.31033 1.98704 6.68933 1.98704 7.24733 2.82404L10.3463 7.47204C10.6543 7.93304 10.6823 8.52304 10.4203 9.01204C10.1583 9.50104 9.65133 9.80404 9.09833 9.80404H2.90133C2.34733 9.80404 1.84032 9.50104 1.57932 9.01204C1.31732 8.52404 1.34632 7.93304 1.65333 7.47204L4.75133 2.82404Z"
                        fill="white"
                    />
                </svg>
            </div>
        );
    }
    if ( item.type === "down" ) {
        return (
            <div className="flex size-5 flex-shrink-0 items-center justify-center rounded-full bg-state-danger">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ transform: "rotate(180deg)" }}>
                    <path
                        d="M4.75133 2.82404C5.31033 1.98704 6.68933 1.98704 7.24733 2.82404L10.3463 7.47204C10.6543 7.93304 10.6823 8.52304 10.4203 9.01204C10.1583 9.50104 9.65133 9.80404 9.09833 9.80404H2.90133C2.34733 9.80404 1.84032 9.50104 1.57932 9.01204C1.31732 8.52404 1.34632 7.93304 1.65333 7.47204L4.75133 2.82404Z"
                        fill="white"
                    />
                </svg>
            </div>
        );
    }

    if ( item.type === "active" ) {
        return (
            <div
                data-guess-no={guessNo}
                className={`flex h-8 flex-shrink-0 items-center gap-1.5 rounded-3xl px-3 cursor-pointer ${
                    item.selected ? "bg-content-primary" : "bg-surface-muted"
                }`}
                onClick={() => guessNo && onGuessChange?.()}
            >
                <LiveIcon className="size-4" />
                <span className={`text-xs font-medium ${item.selected ? "text-surface-card" : "text-t1"}`}>
                    {item.time}
                </span>
            </div>
        );
    }
    if ( item.type === "more" ) {
        return (
            <div
                className={`flex h-8 gap-1 flex-shrink-0 items-center justify-center rounded-3xl px-3 cursor-pointer bg-surface-muted`}
            >
                <span className={`text-xs font-medium text-t1`}>More</span>
                <ChevronDown className="text-content-subtle size-4" />
            </div>
        );
    }

    return (
        <div
            data-guess-no={guessNo}
            className={`flex h-8 flex-shrink-0 items-center justify-center rounded-3xl px-3 cursor-pointer ${
                item.selected ? "bg-content-primary" : "bg-brand-button"
            }`}
            onClick={() => guessNo && onGuessChange?.()}
        >
            <span className={`text-xs font-medium ${item.selected ? "text-surface-card" : "text-t1"}`}>
                {item.time}
            </span>
        </div>
    );
}
